import { pageTitle } from '@palaccount/config';
import { Lock } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';

/**
 * PART 16.7's "no permission" page — the screen a person meets when they open
 * a door their role does not have a key to, however they arrived.
 *
 * THE SENTENCE IS THE SPECIFICATION, not a suggestion: *"You do not have
 * permission to open this page. Ask your administrator."* It lives in
 * `PERM_MESSAGES.deniedPage` and reaches both languages through the
 * dictionary, like every other fixed sentence in the product. (GEN-163
 * words the same page as "You do not have permission to open this." —
 * PART 16.7's fuller sentence is the built one, the usual
 * specific-beats-general reading.)
 *
 * What it must NOT do is as specified as what it says:
 *
 *   BR-PERM-20 — "never show a blank screen, a spinner that never stops, or a
 *     technical error code when a permission is missing." Hence a real page
 *     with a real sentence rather than a redirect to nowhere or a 403 body.
 *
 *   §1.5.2 — "A user who may not see Projects is told about the permission,
 *     not about the subscription — nobody should learn what their company has
 *     and has not bought from an error message meant for someone else." So a
 *     module the company never bought and a screen the role cannot open
 *     produce THE SAME page. That is not laziness; the sameness is the rule.
 *
 *     GEN-21c SAYS THE OPPOSITE — "the two must produce different messages",
 *     with a 'Your company does not have Projects switched on' sentence for
 *     the module case. The two passages cannot both hold; the build follows
 *     §1.5.2, whose reasoning is written out (an error page must not leak
 *     subscription facts to people without the permission), and this comment
 *     is the record that GEN-21c is the contradicted half. GEN-21a, GEN-21b
 *     and GEN-21d are enforced as written in guards.tsx, nav.ts and the
 *     widget catalog
 *     (permission AND module, no third test; a dead module hides from every
 *     role; Multi-branch hides exactly two widgets and no screen).
 *
 *   16.7 — it names no permission and no grid. Telling somebody which switch
 *     they are missing tells them the shape of the company's permission model,
 *     and the person who needs that is the administrator they are being sent
 *     to.
 */
export function NoPermissionPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = pageTitle(t('perm.deniedPageTitle'));
  }, [t]);

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <span
          aria-hidden
          className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground"
        >
          <Lock className="size-5" />
        </span>
        <p className="text-lg font-semibold">{t('perm.deniedPageTitle')}</p>
        <p className="max-w-md text-sm text-muted-foreground">{t('perm.deniedPage')}</p>
      </CardContent>
    </Card>
  );
}

/**
 * 16.7 row 4 / BR-PERM-73 — the landing page for somebody holding no role at
 * all. "Hide every menu group, and say why on the landing page rather than
 * showing an empty shell… An empty product with no explanation reads as a
 * broken product."
 *
 * A separate page from the one above because it is a different situation with
 * a different remedy: that one says a door is closed, this one says nobody has
 * given you any doors yet, and it names the account owner as well as the
 * administrator because in a small company they are the only person who can.
 */
export function NoRolePage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = pageTitle(t('perm.noRoleTitle'));
  }, [t]);

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <span
          aria-hidden
          className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground"
        >
          <Lock className="size-5" />
        </span>
        <p className="text-lg font-semibold">{t('perm.noRoleTitle')}</p>
        <p className="max-w-md text-sm text-muted-foreground">{t('perm.noRoleYet')}</p>
      </CardContent>
    </Card>
  );
}
